import { useFocusRing } from "@react-aria/focus";
import { AriaRadioGroupProps, useRadioGroup } from "@react-aria/radio";
import { useRadioGroupState } from "@react-stately/radio";
import { deepmerge } from "deepmerge-ts";
import React, {
  ComponentPropsWithoutRef,
  ElementRef,
  ForwardedRef,
  PropsWithChildren,
  ReactNode,
  forwardRef,
  useMemo,
} from "react";
import {
  DefaultTheme,
  StyledComponentProps,
  ThemeProvider,
  useTheme,
} from "styled-components";
import { PropsWithPropStyling } from "../../shared";
import { AsProp } from "../../shared/types";
import { ToggleGroupStyle } from "../../themes/components/defaultToggleGroupTheme";
import { LabelProps, Text, TextProps } from "../Typography";
import { Label } from "../Typography/Label";
import { GroupContainer } from "./Elements/GroupContainer";
import { RootContainer } from "./Elements/RootContainer";
import { ToggleGroupContext } from "./ToggleGroupContext";

export type ToggleGroupChangeHandler = (value: string) => void;

export type ToggleGroupVariant = "primary" | "secondary";
export type ToggleGroupSize = "xs" | "s" | "m";

export type ToggleGroupRootBaseProps<
  As extends AsProp,
  DescriptionAs extends AsProp,
  ErrorAs extends AsProp,
  GroupContainerAs extends AsProp,
  LabelAs extends AsProp
> = PropsWithChildren<{
  "aria-label"?: string;
  "aria-labelledby"?: string;
  as?: As;
  description?: ReactNode;
  descriptionAs?: DescriptionAs;
  descriptionProps?: StyledComponentProps<
    DescriptionAs,
    DefaultTheme,
    TextProps,
    never
  >;
  disabled?: boolean;
  error?: ReactNode;
  errorAs?: ErrorAs;
  errorProps?: StyledComponentProps<ErrorAs, DefaultTheme, TextProps, never>;
  id?: string;
  groupContainerAs?: GroupContainerAs;
  groupContainerProps?: StyledComponentProps<
    GroupContainerAs,
    DefaultTheme,
    PropsWithPropStyling,
    never
  >;
  label?: ReactNode;
  labelAs?: LabelAs;
  labelProps?: StyledComponentProps<LabelAs, DefaultTheme, LabelProps, never>;
  name?: string;
  onChange?: ToggleGroupChangeHandler;
  theme?: Partial<ToggleGroupStyle>;
  value?: string;
  size?: ToggleGroupSize;
  variant?: ToggleGroupVariant;
}>;

export type ToggleGroupRootProps<
  As extends AsProp = any,
  DescriptionAs extends AsProp = any,
  ErrorAs extends AsProp = any,
  GroupContainerAs extends AsProp = any,
  LabelAs extends AsProp = any
> = PropsWithPropStyling<
  ToggleGroupRootBaseProps<
    As,
    DescriptionAs,
    ErrorAs,
    GroupContainerAs,
    LabelAs
  > &
    Omit<ComponentPropsWithoutRef<As>, "onChange">
>;

export const ToggleGroupRoot = forwardRef(renderToggleGroupRoot) as <
  As extends AsProp = "div",
  DescriptionAs extends AsProp = "div",
  ErrorAs extends AsProp = "div",
  GroupContainerAs extends AsProp = "div",
  LabelAs extends AsProp = "label"
>(
  props: ToggleGroupRootProps<
    As,
    DescriptionAs,
    ErrorAs,
    GroupContainerAs,
    LabelAs
  > & {
    ref?: ForwardedRef<ElementRef<As>>;
  }
) => ReturnType<typeof renderToggleGroupRoot>;

let instances = 0;

function renderToggleGroupRoot<
  As extends AsProp,
  DescriptionAs extends AsProp,
  ErrorAs extends AsProp,
  GroupContainerAs extends AsProp,
  LabelAs extends AsProp
>(
  {
    "aria-label": ariaLabel,
    "aria-labelledby": ariaLabelledBy,
    as,
    children,
    description,
    descriptionAs,
    descriptionProps: descriptionPropsProp,
    disabled = false,
    error,
    errorAs,
    errorProps,
    id,
    groupContainerAs,
    groupContainerProps,
    label,
    labelAs,
    labelProps,
    name: nameProp,
    onChange,
    theme: customTheme,
    value,
    variant: variantProp,
    size = "m",
    ...containerProps
  }: ToggleGroupRootProps<
    As,
    DescriptionAs,
    ErrorAs,
    GroupContainerAs,
    LabelAs
  >,
  ref: ForwardedRef<ElementRef<As>>
) {
  const name = useMemo(() => nameProp ?? `toggle-group-${++instances}`, [
    nameProp,
  ]);

  const hookProps: AriaRadioGroupProps = {
    "aria-label": ariaLabel,
    "aria-labelledby": ariaLabelledBy,
    description,
    errorMessage: error,
    isDisabled: disabled,
    id,
    label,
    name,
    onChange,
    orientation: "horizontal",
    validationState: error ? "invalid" : "valid",
    value,
  };

  const { focusProps, isFocusVisible } = useFocusRing({ within: true });

  const state = useRadioGroupState(hookProps);

  const {
    descriptionProps,
    errorMessageProps,
    labelProps: labelRadioProps,
    radioGroupProps,
  } = useRadioGroup(hookProps, state);

  const theme = useTheme();

  const {
    components: { toggleGroup: toggleGroupTheme },
  } = theme;

  const variant =
    variantProp ?? toggleGroupTheme.defaultProps?.variant ?? "secondary";

  const currentStyle = useMemo(
    () =>
      deepmerge(
        toggleGroupTheme.defaultStyle ?? {},
        toggleGroupTheme.variant?.[variant] ?? {},
        customTheme ?? {}
      ),
    [customTheme && JSON.stringify(customTheme), toggleGroupTheme, variant]
  );

  const currentTheme = useMemo(
    () =>
      deepmerge(theme, {
        components: { toggleGroup: { current: currentStyle } },
      }) as DefaultTheme,
    [currentStyle, theme]
  );

  const contextValue = useMemo(() => ({ state, size }), [state, size]);

  return (
    <ThemeProvider theme={currentTheme}>
      <RootContainer
        as={as as undefined}
        {...radioGroupProps}
        {...containerProps}
        ref={ref}
      >
        {label && (
          <Label
            $minHeight="3sp"
            variant={
              hookProps.validationState === "invalid" ? "danger" : undefined
            }
            {...labelRadioProps}
            {...labelProps}
            as={labelAs as undefined}
          >
            {label}
          </Label>
        )}
        <GroupContainer
          isFocusVisible={isFocusVisible}
          isInvalid={hookProps.validationState === "invalid"}
          {...focusProps}
          {...groupContainerProps}
          as={groupContainerAs as undefined}
          size={size}
        >
          <ToggleGroupContext.Provider value={contextValue}>
            {children}
          </ToggleGroupContext.Provider>
        </GroupContainer>
        {description && (
          <Text
            $mt="0.5sp"
            size="s"
            {...descriptionProps}
            {...descriptionPropsProp}
            as={descriptionAs ?? "div"}
          >
            {description}
          </Text>
        )}
        {error && (
          <Text
            $mt="0.5sp"
            size="s"
            variant="danger"
            {...errorMessageProps}
            {...errorProps}
            as={errorAs ?? "div"}
          >
            {error}
          </Text>
        )}
      </RootContainer>
    </ThemeProvider>
  );
}
